/**
 * The EU taxonomy figures, and the check they have to survive.
 *
 * Three ratios, prescribed: the share of turnover, of capital expenditure and
 * of operating expenditure that is taxonomy eligible, and of that, the share
 * that is aligned. Exactly three, in that order, because the regulation says
 * so; they are not rows somebody adds.
 *
 * The percentages are derived from the amounts rather than typed. A statement
 * that reports 40% eligible next to amounts that come to 38% is one nobody can
 * tell which half of to believe, and both halves are audited.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **Aligned is a subset of eligible.** An activity has to be eligible before
 * it can be aligned, so aligned can never exceed it. That is the single
 * commonest arithmetic error in a taxonomy disclosure, it is invisible in a
 * table of four numbers, and it is caught here.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Pure. Amounts in, shares out.
 */

import { readFigure } from '@/domain/progress'

/** The three the regulation asks for, in the order it asks for them. */
export const TAXONOMY_KPIS = ['turnover', 'capex', 'opex'] as const

export type TaxonomyKpiId = (typeof TAXONOMY_KPIS)[number]

/** One of the three, as the author enters it. */
export interface TaxonomyKpi {
  readonly id: TaxonomyKpiId
  /** The denominator: total turnover, total CapEx, total OpEx. */
  total: string
  eligible: string
  aligned: string
}

/** One of the three, worked out. */
export interface TaxonomyShare {
  readonly id: TaxonomyKpiId
  readonly total: number | null
  readonly eligible: number | null
  readonly aligned: number | null
  /** Between 0 and 1, or null where it cannot be worked out. */
  readonly eligibleShare: number | null
  readonly alignedShare: number | null
  /**
   * Aligned exceeding eligible, which cannot happen.
   *
   * Reported rather than corrected. Deciding which of the two figures is wrong
   * is not this module's business, and quietly capping one would hide the
   * error rather than raise it.
   */
  readonly impossible: boolean
}

/** A share of a total, or null when either figure is missing. */
function share(part: number | null, total: number | null): number | null {
  if (part === null || total === null || total === 0) return null
  return part / total
}

/** One of the three, worked out from what was entered. */
export function shareOf(kpi: TaxonomyKpi): TaxonomyShare {
  const total = readFigure(kpi.total)
  const eligible = readFigure(kpi.eligible)
  const aligned = readFigure(kpi.aligned)

  return {
    id: kpi.id,
    total,
    eligible,
    aligned,
    eligibleShare: share(eligible, total),
    alignedShare: share(aligned, total),
    impossible: eligible !== null && aligned !== null && aligned > eligible,
  }
}

/** All three, in the order the regulation asks for them. */
export function sharesOf(kpis: readonly TaxonomyKpi[]): readonly TaxonomyShare[] {
  return TAXONOMY_KPIS.map((id) => {
    const found = kpis.find((kpi) => kpi.id === id)
    return shareOf(found ?? { id, total: '', eligible: '', aligned: '' })
  })
}

/** A fresh set: the three, empty. */
export function newTaxonomyKpis(): TaxonomyKpi[] {
  return TAXONOMY_KPIS.map((id) => ({ id, total: '', eligible: '', aligned: '' }))
}
